import React, { useState, useEffect, useCallback } from "react";
import {
  CircularProgress,
  Container,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import UploadFile from "../components/UploadFile";

import readXlsxFile from "read-excel-file";
import { useDispatch, useSelector } from "react-redux";

import { fetch_products_data_action } from "./../../../redux/actions/products";

export const Sync = (props) => {
  const dispatch = useDispatch();
  const productsStore = useSelector((state) => state.products || []);

  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [fileData, setFileData] = useState([]);
  const [fileProcessing, setFileProcessing] = useState(false);
  const [comparisonResults, setComparisonResults] = useState([]);
  const [filters, setFilters] = useState({
    taxDifference: false,
    existing: false,
  });

  const handleChangeData = async (data, file) => {
    setFileProcessing(true);
    let dataImported = [];

    switch (file.type) {
      case "text/csv":
        data
          .map((item) => item.data)
          .forEach((item) => {
            if (item.length > 1) {
              dataImported.push(item);
            }
          });

        break;
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
        await readXlsxFile(file).then((data) => {
          data.forEach((item) => {
            if (item.length > 1) {
              dataImported.push(item);
            }
          });
        });

        break;
      default:
        setErrorInfo({
          error: true,
          message: "Unsupported file type. Please usload a .csv or .xlsx file.",
        });
    }

    if (!dataImported.length) {
      setErrorInfo({
        error: true,
        message:
          "The imported file couldn't be processed (it didn't generate any data to be imported).",
      });
      return;
    } else {
      const headersIndex = dataImported.findIndex((item) =>
        String(item).toLowerCase().includes("sales tax code")
      );

      if (headersIndex === -1) {
        setErrorInfo({
          error: true,
          message:
            "The imported file couldn't be processed (couldn't find the headers from the imported data: sales tax code).",
        });
        return;
      }

      const headersRow = dataImported[headersIndex].map((item) =>
        String(item || "")
          .trim()
          .toLowerCase()
      );

      const headersMap = {
        item_code: headersRow.indexOf("item"),
        description: headersRow.indexOf("description"),
        sales_tax_code: headersRow.indexOf("sales tax code"),
      };

      const rowsMap = dataImported.slice(headersIndex + 1).map((row) => ({
        item_code: String(row[headersMap.item_code] || "").trim(),
        description: String(row[headersMap.description] || "").trim(),
        sales_tax_code: String(row[headersMap.sales_tax_code] || "").trim(),
      }));

      setFileData(rowsMap);
    }
  };

  const handleChangeFilters = (e) => {
    const { name, checked } = e.target;

    setFilters((prevState) => ({ ...prevState, [name]: checked }));
  };

  const errorFallback = () => {
    setErrorInfo({ error: false, message: "" });
    setFileData([]);
    setFileProcessing(false);
    setComparisonResults([]);
  };

  const compareFileData = useCallback(() => {
    const comparisonData = fileData.map((item) => {
      const productData = productsStore.find(
        (pItem) => pItem?.item_code?.item_code === item.item_code
      );

      const qbItemCode = item.item_code;

      const qbDescription = item.description;

      const productFound = Boolean(productData);

      const qbTaxToBool =
        String(item.sales_tax_code || "").toLowerCase() === "tax";

      const productTax = productFound ? productData.tax_default : false;

      const taxMatch = Boolean(productTax) === qbTaxToBool;

      return {
        item_code: qbItemCode,
        description: qbDescription,
        qbtax: qbTaxToBool,
        ptax: productTax,
        tax_match: taxMatch,
        exist: productFound,
      };
    });

    setComparisonResults(comparisonData);
    setFileProcessing(false);
  }, [fileData, productsStore]);

  useEffect(() => {
    if (!productsStore.length) {
      dispatch(fetch_products_data_action(productsStore));
    }
  }, [dispatch, productsStore]);

  useEffect(() => {
    if (fileData.length && productsStore.length && !errorInfo.error) {
      compareFileData();
    }
  }, [fileData, productsStore, errorInfo, compareFileData]);

  return (
    <Container>
      {!productsStore.length ? (
        <div align="center">
          <br />
          <CircularProgress />
          <div>
            Loading products <br />
          </div>
        </div>
      ) : !fileData.length && !fileProcessing ? (
        <UploadFile
          {...props}
          errorInfo={errorInfo}
          setErrorInfo={setErrorInfo}
          handleChangeData={handleChangeData}
        />
      ) : fileProcessing ? (
        <div align="center">
          <br />
          <CircularProgress />
          <div>Processing file data</div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              <div>
                <Typography component="h4" color="error">
                  {errorInfo.message}
                </Typography>
              </div>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => errorFallback()}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <></>
      )}
      {!errorInfo.error && comparisonResults.length ? (
        <>
          <Typography component="h6" variant="h6" align="center">
            {`Quickbooks vs Software comparison`}
          </Typography>
          <Typography variant="subtitle1">
            <strong>Filtering:</strong>
          </Typography>
          <FormControlLabel
            value="start"
            control={
              <Checkbox
                name="taxDifference"
                value={filters.taxDifference}
                checked={filters.taxDifference}
                onChange={handleChangeFilters}
                color="primary"
              />
            }
            label="Show tax differences"
            labelPlacement="end"
          />
          <FormControlLabel
            value="start"
            control={
              <Checkbox
                name="existing"
                value={filters.existing}
                checked={filters.existing}
                onChange={handleChangeFilters}
                color="primary"
              />
            }
            label="Show existing"
            labelPlacement="end"
          />
          <TableContainer style={{ maxHeight: "80vh" }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell size="small">Item Code</TableCell>
                  <TableCell size="small">Description</TableCell>
                  <TableCell size="small">QB Tax</TableCell>
                  <TableCell size="small">Product Tax</TableCell>
                  <TableCell size="small">Product Exists</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonResults
                  .filter((item, index) => {
                    let display = true;

                    if (filters.existing && !item.exist) {
                      display = false;
                    }

                    if (filters.taxDifference && item.tax_match) {
                      display = false;
                    }

                    return display;
                  })
                  .map((item, index) => (
                    <TableRow key={`row-${index}`} hover tabIndex={-1}>
                      <TableCell size="small">{item.item_code}</TableCell>
                      <TableCell size="small">{item.description}</TableCell>
                      <TableCell
                        size="small"
                        style={{
                          background: item.tax_match
                            ? "lightgreen"
                            : "lightyellow",
                          textAlign: "center",
                        }}
                      >
                        {item.qbtax ? <CheckIcon /> : <CloseIcon />}
                      </TableCell>
                      <TableCell
                        size="small"
                        style={{
                          background: item.tax_match
                            ? "lightgreen"
                            : "lightyellow",
                          textAlign: "center",
                        }}
                      >
                        {item.ptax ? <CheckIcon /> : <CloseIcon />}
                      </TableCell>
                      <TableCell
                        size="small"
                        style={{
                          background: item.exist ? "lightgreen" : "lightyellow",
                          textAlign: "center",
                        }}
                      >
                        {item.exist ? <CheckIcon /> : <CloseIcon />}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <></>
      )}
    </Container>
  );
};

export default Sync;
